import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db } from '../firebase';
import { updateProfile, updateEmail, updatePassword, signOut } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import efafcLogo from '../assets/efafc.png';
import { useAuth } from '../context/AuthContext';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    pseudo: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
        setForm(prev => ({
          ...prev,
          pseudo: user.displayName || '',
          email: user.email || ''
        }));
      } else {
        navigate('/auth');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Mise à jour du pseudo
      if (form.pseudo !== user.displayName) {
        await updateProfile(user, { displayName: form.pseudo });
        await updateDoc(doc(db, "users", user.uid), {
          pseudo: form.pseudo
        });
      }

      // Mise à jour de l'email
      if (form.email !== user.email) {
        await updateEmail(user, form.email);
        await updateDoc(doc(db, "users", user.uid), {
          email: form.email
        });
      }

      // Mise à jour du mot de passe
      if (form.newPassword && form.newPassword === form.confirmPassword) {
        await updatePassword(user, form.newPassword);
      } else if (form.newPassword) {
        setError("Les mots de passe ne correspondent pas");
        return;
      }

      setSuccess("Profil mis à jour avec succès !");
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogout = async () => {
    try {
      setSuccess("Déconnexion en cours...");
      await signOut(auth);
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#001B56] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#BDFF00]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#001B56] relative">
      {/* Bouton retour */}
      <button
        onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-1 text-white hover:text-[#BDFF00] font-bold text-base z-50 bg-transparent p-0 border-none shadow-none"
        style={{background:'none', boxShadow:'none'}}
        type="button"
      >
        <span style={{fontSize:'1.5rem', lineHeight:'1'}}>&larr;</span>
        Retour
      </button>
      <div className="absolute top-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
        <Link to="/">
          <img src={efafcLogo} alt="EFAFC Logo" className="h-20 mb-2 drop-shadow-xl hover:scale-105 transition cursor-pointer" />
        </Link>
      </div>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl px-8 py-10 flex flex-col items-center mt-32">
        {/* Bouton admin si admin */}
        {userData?.role === 'admin' && (
          <button
            className="w-full mb-6 bg-[#00238D] hover:bg-[#001B56] text-[#FFE400] font-bold py-2 rounded-lg text-lg shadow transition"
            onClick={() => navigate('/admin')}
          >
            Accéder à l'espace admin
          </button>
        )}
        <h1 className="text-3xl font-bold text-[#001B56] mb-8 text-center">Mon Profil</h1>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 w-full">
            <XCircle className="text-red-500" size={20} />
            <span className="text-red-600">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 w-full">
            <CheckCircle2 className="text-green-500" size={20} />
            <span className="text-green-600">{success}</span>
          </div>
        )}

        <form onSubmit={handleUpdateProfile} className="w-full space-y-6">
          <div>
            <label htmlFor="pseudo" className="block text-[#001B56] font-semibold mb-1">Pseudo</label>
            <input
              type="text"
              id="pseudo"
              name="pseudo"
              value={form.pseudo}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#BDFF00] focus:ring-2 focus:ring-[#BDFF00] outline-none text-[#001B56] bg-gray-50"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-[#001B56] font-semibold mb-1">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#BDFF00] focus:ring-2 focus:ring-[#BDFF00] outline-none text-[#001B56] bg-gray-50"
            />
          </div>

          <div className="relative">
            <label htmlFor="newPassword" className="block text-[#001B56] font-semibold mb-1">Nouveau mot de passe</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="newPassword"
              name="newPassword"
              value={form.newPassword}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#BDFF00] focus:ring-2 focus:ring-[#BDFF00] outline-none text-[#001B56] bg-gray-50"
              placeholder="Laisser vide pour ne pas changer"
            />
            <button
              type="button"
              className="absolute right-3 top-9 text-[#001B56] hover:text-[#BDFF00]"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-[#001B56] font-semibold mb-1">Confirmer le mot de passe</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#BDFF00] focus:ring-2 focus:ring-[#BDFF00] outline-none text-[#001B56] bg-gray-50"
              placeholder="Laisser vide pour ne pas changer"
            />
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              className="flex-1 bg-[#BDFF00] hover:bg-[#FFE400] text-[#001B56] font-bold py-3 rounded-lg text-lg shadow transition"
            >
              Mettre à jour
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-lg text-lg shadow transition"
            >
              Se déconnecter
            </button>
          </div>
        </form>
      </div>
      <footer className="w-full text-center text-white text-xs mt-10 opacity-60">
        © {new Date().getFullYear()} Entente Feignies Aulnoye FC. Tous droits réservés.
      </footer>
    </div>
  );
} 